import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { categories } from '../data/mock';
import {
  Search,
  Star,
  ShoppingCart,
  Filter,
  Loader2,
  Download,
  Shield,
  Clock,
  TrendingUp,
  Users,
  Sparkles,
  Zap,
  CheckCircle2,
  Bot,
  Flame,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Marketplace = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [viewerCount, setViewerCount] = useState(2341);
  const [countdown, setCountdown] = useState({ h: 23, m: 47, s: 12 });

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/products`);
      setApps(data);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Friendly rescue message if the user landed back here from a canceled/declined Stripe checkout
  useEffect(() => {
    if (searchParams.get('checkout') === 'canceled') {
      toast.info('Card declined or checkout canceled?', {
        description: 'Try again with Cash App Pay, Klarna (Buy Now Pay Later), Apple Pay, or a different card — all available at checkout.',
        duration: 8000,
      });
      // Clean the URL so this doesn't fire on every re-render
      window.history.replaceState({}, '', '/marketplace');
    }
  }, [searchParams]);

  // Live viewer count + countdown jitter for urgency
  useEffect(() => {
    const id = setInterval(() => {
      setViewerCount((v) => Math.max(1800, Math.min(3200, v + Math.floor(Math.random() * 11) - 5)));
      setCountdown((c) => {
        let { h, m, s } = c;
        if (s > 0) s -= 1;
        else if (m > 0) { m -= 1; s = 59; }
        else if (h > 0) { h -= 1; m = 59; s = 59; }
        else { h = 23; m = 59; s = 59; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const filteredApps = useMemo(() => apps.filter((app) => {
    const matchesCategory = selectedCategory === 'all' || app.category === selectedCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || app.name.toLowerCase().includes(q) || app.description.toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  }), [apps, selectedCategory, searchQuery]);

  const handleCheckout = async (app) => {
    setCheckingOut(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${BACKEND_URL}/api/payments/checkout/session`, {
        product_id: app.id,
        origin_url: origin,
        quantity: 1,
      });
      window.location.href = data.url;
    } catch (error) {
      toast.error('Failed to start checkout', {
        description: 'Please try again or contact support',
      });
      setCheckingOut(false);
    }
  };

  const handleSubscribe = useCallback(async () => {
    // If not logged in, send to login with redirect back here + action=subscribe
    if (!isAuthenticated) {
      toast.info('Create an account or log in to subscribe', { duration: 2500 });
      navigate('/login?redirect=/marketplace&action=subscribe');
      return;
    }
    setCheckingOut(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${BACKEND_URL}/api/subscriptions/create`, {
        origin_url: origin,
      }, { withCredentials: true });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.success("You're already subscribed!", { description: 'Taking you to the AI Lab...' });
        navigate('/ai-lab');
      }
    } catch (error) {
      toast.error('Failed to start subscription', {
        description: 'Please try again or contact support',
      });
      setCheckingOut(false);
    }
  }, [isAuthenticated, navigate]);

  // Auto-trigger subscribe after login bounce-back
  useEffect(() => {
    if (!authLoading && isAuthenticated && searchParams.get('action') === 'subscribe') {
      handleSubscribe();
    }
  }, [authLoading, isAuthenticated, searchParams, handleSubscribe]);

  const pad = (n) => String(n).padStart(2, '0');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] pt-24 pb-20 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-orange-500 animate-spin" data-testid="marketplace-loader" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#11111a] to-[#0a0a0f] pt-24 pb-20 text-white relative overflow-hidden" data-testid="marketplace-page">
      {/* Ambient gradient blobs */}
      <div className="pointer-events-none absolute top-20 -left-24 w-96 h-96 rounded-full bg-orange-500/20 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 -right-24 w-[28rem] h-[28rem] rounded-full bg-red-600/20 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 w-[32rem] h-[32rem] rounded-full bg-amber-500/10 blur-[160px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Flash Sale Bar */}
        <div className="mb-6 rounded-2xl border border-orange-500/40 bg-gradient-to-r from-orange-600/20 via-red-600/20 to-amber-500/20 backdrop-blur p-4 flex flex-wrap items-center justify-between gap-4" data-testid="flash-sale-bar">
          <div className="flex items-center gap-3">
            <Flame className="w-6 h-6 text-orange-400 animate-pulse" />
            <div>
              <div className="text-sm font-bold text-orange-200">FLASH SALE — AI Bundle Pricing</div>
              <div className="text-xs text-orange-100/70">Locked-in launch pricing ends soon</div>
            </div>
          </div>
          <div className="flex items-center gap-2 font-mono">
            <Clock className="w-4 h-4 text-orange-300" />
            {['h', 'm', 's'].map((k) => (
              <span key={k} className="bg-black/40 border border-orange-500/40 rounded-md px-2 py-1 text-orange-100 text-sm font-bold tabular-nums">
                {pad(countdown[k])}
              </span>
            ))}
          </div>
        </div>

        {/* Trust Banner */}
        <div className="mb-10 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6" data-testid="trust-banner">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <Users className="w-6 h-6 text-orange-400 mb-1" />
              <div className="text-2xl font-bold text-white">12,547+</div>
              <div className="text-xs text-white/60">Happy Customers</div>
            </div>
            <div className="flex flex-col items-center">
              <Download className="w-6 h-6 text-orange-400 mb-1" />
              <div className="text-2xl font-bold text-white">28,392</div>
              <div className="text-xs text-white/60">Downloads</div>
            </div>
            <div className="flex flex-col items-center">
              <Star className="w-6 h-6 fill-yellow-400 text-yellow-400 mb-1" />
              <div className="text-2xl font-bold text-white">4.9/5.0</div>
              <div className="text-xs text-white/60">Average Rating</div>
            </div>
            <div className="flex flex-col items-center">
              <Shield className="w-6 h-6 text-emerald-400 mb-1" />
              <div className="text-sm font-bold text-white">30-Day Guarantee</div>
              <div className="text-xs text-white/60">100% Money Back</div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="mb-10 text-center md:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs font-semibold mb-5" data-testid="ai-marketplace-pill">
            <Bot className="w-3.5 h-3.5" /> AI-POWERED MARKETPLACE
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight tracking-tight">
            <span className="bg-gradient-to-r from-orange-400 via-amber-300 to-red-500 bg-clip-text text-transparent">
              Build Wealth With AI
            </span>
            <br />
            <span className="text-white">— While You Sleep.</span>
          </h1>
          <p className="text-base md:text-lg text-white/70 mt-4 max-w-2xl">
            Premium AI-powered playbooks, prompts &amp; auto-posting tools used by 12,000+ creators to print money in 2026.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded-full">
            <Clock className="w-4 h-4 animate-pulse" />
            <span className="font-semibold text-sm">{viewerCount.toLocaleString()} people viewing right now</span>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mb-8 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 w-5 h-5" />
            <Input
              data-testid="marketplace-search"
              placeholder="Search AI products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 text-base bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500"
            />
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <Filter className="w-5 h-5 text-white/50 flex-shrink-0" />
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                data-testid={`category-${category.id}`}
                variant="outline"
                className={
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white border-transparent hover:opacity-90'
                    : 'border-white/15 bg-white/5 text-white/80 hover:border-orange-500 hover:text-white'
                }
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <p className="text-white/60 text-sm">
            Showing <span className="font-semibold text-white">{filteredApps.length + 1}</span> AI products
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
          {/* AI Auto-Poster Subscription Card (Hero) */}
          <Card
            data-testid="ai-autoposter-card"
            className="overflow-hidden relative bg-gradient-to-br from-orange-500/10 via-red-600/10 to-amber-500/10 border border-orange-500/40 backdrop-blur-md hover:shadow-[0_0_60px_rgba(249,115,22,0.35)] transition-all duration-500 group"
          >
            <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-orange-500/30 blur-3xl group-hover:bg-orange-500/40 transition-all" />
            <div className="absolute top-3 left-3 z-10">
              <Badge className="bg-gradient-to-r from-orange-500 to-red-600 text-white border-0 shadow-lg animate-pulse">
                <Bot className="w-3 h-3 mr-1" /> AI POWERED
              </Badge>
            </div>
            <div className="absolute top-3 right-3 z-10">
              <Badge className="bg-emerald-500 text-white border-0 shadow-lg">MOST POPULAR</Badge>
            </div>

            <div className="relative h-56 overflow-hidden flex items-center justify-center">
              <div className="text-center relative z-10">
                <div className="w-24 h-24 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-2xl shadow-orange-500/40 group-hover:scale-110 transition-transform">
                  <Sparkles className="w-12 h-12 text-white" />
                </div>
                <div className="text-2xl font-black text-white">AI Auto-Poster</div>
                <div className="text-xs text-orange-200/80 mt-1">GPT-5.1 • Whisper • Vision</div>
              </div>
            </div>

            <CardHeader className="relative z-10">
              <div className="flex items-end justify-between mb-2">
                <Badge variant="outline" className="border-orange-400/50 text-orange-300 bg-orange-500/10">
                  AI Subscription
                </Badge>
                <div className="text-right">
                  <div className="text-3xl font-black text-white">
                    $29.99<span className="text-base font-medium text-white/60">/mo</span>
                  </div>
                  <div className="text-[11px] text-emerald-400 font-semibold">Cancel anytime</div>
                </div>
              </div>
              <CardTitle className="text-xl text-white">AI Social Media Auto-Poster</CardTitle>
              <CardDescription className="text-white/70 leading-relaxed">
                Generate and auto-post engaging content to Facebook &amp; Instagram using GPT-5.1 AI. Save hours daily creating professional posts instantly.
              </CardDescription>

              <ul className="mt-4 space-y-2 text-sm">
                {[
                  'Unlimited AI-generated posts',
                  'Direct Facebook + Instagram posting',
                  'AI image generation included',
                  'Voice-to-post with Whisper AI',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-white/80">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </CardHeader>

            <CardContent className="relative z-10">
              <Button
                data-testid="subscribe-autoposter-btn"
                onClick={handleSubscribe}
                disabled={checkingOut}
                size="lg"
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-6 shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all"
              >
                {checkingOut ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" /> Start Free Trial — $29.99/mo
                  </>
                )}
              </Button>
              <p className="text-center text-xs text-white/50 mt-3 flex items-center justify-center gap-2">
                <Lock className="w-3 h-3" /> Secure checkout • Cancel anytime
              </p>
            </CardContent>
          </Card>

          {filteredApps.map((app, idx) => (
            <Card
              key={app.id}
              data-testid={`product-card-${app.id}`}
              className="overflow-hidden relative bg-white/5 border border-white/10 backdrop-blur-md hover:border-orange-500/60 hover:shadow-[0_0_40px_rgba(249,115,22,0.2)] transition-all duration-500 group"
            >
              {app.ai_powered && (
                <div className="absolute top-3 left-3 z-10">
                  <Badge className="bg-gradient-to-r from-orange-500 to-red-600 text-white border-0 shadow-lg">
                    <Bot className="w-3 h-3 mr-1" /> AI POWERED
                  </Badge>
                </div>
              )}
              {idx === 0 && (
                <div className="absolute top-3 right-3 z-10">
                  <Badge className="bg-yellow-500 text-black border-0 shadow-lg font-bold">⭐ BEST SELLER</Badge>
                </div>
              )}

              <div className="relative h-56 overflow-hidden">
                <img
                  src={app.image}
                  alt={app.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                  <div>
                    {app.tagline && (
                      <div className="text-[11px] uppercase tracking-wider text-orange-300 font-bold mb-1" data-testid={`product-tagline-${app.id}`}>
                        {app.tagline}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-white/80">
                      <Users className="w-3 h-3" /> {app.downloads} sold
                      <span className="text-white/30">•</span>
                      <TrendingUp className="w-3 h-3 text-emerald-400" /> Hot seller
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-orange-500 to-red-600 px-3 py-1.5 rounded-lg shadow-xl">
                    <span className="text-xl font-black text-white">${app.price}</span>
                  </div>
                </div>
              </div>

              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-xs capitalize border-white/15 text-white/70 bg-white/5">
                    {app.category}
                  </Badge>
                  <div className="flex items-center gap-1 bg-yellow-500/10 border border-yellow-500/30 px-2 py-1 rounded">
                    <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm font-bold text-yellow-200">{app.rating}</span>
                  </div>
                </div>
                <CardTitle className="text-lg text-white group-hover:text-orange-300 transition-colors leading-snug">
                  {app.name}
                </CardTitle>
                <CardDescription className="text-sm text-white/70 leading-relaxed line-clamp-3">
                  {app.description}
                </CardDescription>

                {app.highlights && app.highlights.length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {app.highlights.slice(0, 4).map((h) => (
                      <li key={h} className="flex items-start gap-2 text-xs text-white/80">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* 🎁 Prompt Vault bonus badge */}
                <div className="mt-3 rounded-lg bg-gradient-to-r from-orange-500/15 to-amber-500/10 border border-orange-500/30 px-3 py-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-base">🎁</span>
                    <div>
                      <div className="text-orange-300 font-bold">BONUS: 15 AI Prompts Included</div>
                      <div className="text-white/50 text-[10px]">Ready-to-paste for ChatGPT/Claude/Gemini</div>
                    </div>
                  </div>
                  <Link
                    to={`/prompt-vault/${app.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[10px] font-bold text-orange-300 hover:text-orange-200 underline whitespace-nowrap"
                    data-testid={`vault-preview-${app.id}`}
                  >
                    Peek →
                  </Link>
                </div>
              </CardHeader>

              <CardContent>
                <Button
                  data-testid={`buy-product-${app.id}`}
                  onClick={() => handleCheckout(app)}
                  disabled={checkingOut}
                  size="lg"
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-5 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 transition-all"
                >
                  {checkingOut ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5 mr-2" /> Get Instant Access — ${app.price}
                    </>
                  )}
                </Button>
                <p className="text-center text-xs text-white/40 mt-3 flex items-center justify-center gap-2">
                  <Lock className="w-3 h-3" /> Secure • <Zap className="w-3 h-3" /> Instant • <Shield className="w-3 h-3" /> Guaranteed
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredApps.length === 0 && (
          <div className="text-center py-20" data-testid="empty-state">
            <Search className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">No AI products found</h3>
            <p className="text-white/60 mb-6">Try adjusting your search or filters</p>
            <Button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              variant="outline"
              className="border-orange-500/50 text-orange-300 hover:bg-orange-500/10"
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </Button>
          </div>
        )}

        {checkingOut && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white/10 border border-white/20 backdrop-blur-xl p-6 rounded-xl">
              <div className="flex items-center space-x-3">
                <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
                <span className="text-lg text-white font-medium">Redirecting to secure checkout...</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Marketplace;
